import { Outlet, useNavigate, useParams } from "react-router-dom";
import {
  AiDeploysUrl,
  AiDeploysAttachedDataUrl,
  AiDeploysAppInformationUrl,
} from "../../utils/urls";
import RightArrow from "./../../assets/icons/arrow-right.svg?react";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Helmet } from "react-helmet";
import { Skeleton, TabOptions, Tabs, Typography } from "djuno-design";
import NoticeIcon from "./../../assets/icons/logs/notice.svg?react";
import ChainIcon from "./../../assets/icons/chain.svg?react";
import {
  getAiAppAsync,
  handleClearAiAppSlice,
  selectAiApp,
  selectAiAppLoading,
} from "../../store/ai/deploys/appSlice";
import { AiAppsRefreshStatus } from "../../store/ai/deploys/appsSlice";

export const generateServiceRoutes = (appId: string) => {
  const options: TabOptions = [];

  const generalInformationOption = {
    label: (
      <div className="flex items-center gap-1">
        <NoticeIcon className="w-3" />
        General information
      </div>
    ),
    url: AiDeploysAppInformationUrl(appId),
    testId: "1th-tab",
  };
  const attachedDataOption = {
    label: (
      <div className="flex items-center gap-1">
        <ChainIcon className="w-3" />
        Attached data
      </div>
    ),
    url: AiDeploysAttachedDataUrl(appId),
    testId: "2th-tab",
  };

  options.push(generalInformationOption);
  options.push(attachedDataOption);

  return options;
};

const AiAppPage = () => {
  const { appId, engine } = useParams();

  const app = useAppSelector(selectAiApp);
  const appLoading = useAppSelector(selectAiAppLoading);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const AiAppPageMenus = useMemo(() => {
    if (appId) {
      return generateServiceRoutes(appId);
    }
    return [];
  }, [appId]);

  useEffect(() => {
    if (appId) {
      dispatch(getAiAppAsync({ id: appId }));
    }

    return () => {
      dispatch(handleClearAiAppSlice());
    };
  }, [dispatch, appId, engine]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (app && AiAppsRefreshStatus.includes(app.status.state)) {
      interval = setInterval(() => {
        dispatch(
          getAiAppAsync({
            id: app.id,
            withoutLoading: true,
          })
        );
      }, 20000);
    } else {
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, app]);

  return (
    <>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | AI App ({app ? app.spec.name : ""})
        </title>
        <meta name="description" content="" />
      </Helmet>
      <div className="flex items-center justify-between h-16">
        <div className="px-6 items-center justify-between flex flex-1 transition duration-150">
          <div className="flex items-center gap-2">
            <RightArrow
              onClick={() => {
                navigate(AiDeploysUrl);
              }}
              className="rotate-180 w-5 h-5 hover:scale-110 transition-all duration-500 text-slate-800 dark:text-slate-100 cursor-pointer"
            />
            {app && !appLoading && (
              <>
                <div className="flex flex-col">
                  <Typography.Title level={5} className="!mb-0">
                    {app.spec.name}
                  </Typography.Title>
                </div>
              </>
            )}
            {appLoading && (
              <>
                <Skeleton
                  shape="rectangle"
                  style={{ width: 200, height: 30 }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 mt-5">
        <div className="h-11">
          <Tabs options={AiAppPageMenus} />
        </div>
        <div className="py-6">
          <Outlet />
        </div>
      </div>
    </>
  );
};
export default AiAppPage;
